import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate, useLocation } from 'react-router-dom';
import classNames from 'classnames';
import { format } from 'date-fns';
import Button from 'carbon-react/lib/components/button';

import { UILoader, UISystemIcon, toastError } from '@futrli/ui-components';
import { Logger, QUERY_PARAMS, ROUTES, getForecastByIdOnboardingUrl } from '@futrli/shared-services';

import * as styles from './onboarding.scss';
import { OnboardingPageTitle } from '../../../../predict/src/forecast/onboarding/components/onboarding-page-title';
import {
  LS_FORECASTING_ONBOARDING_STEP,
  LS_FORECASTING_ONBOARDING_TYPE,
  ONBOARDING_FORECAST_TYPE,
  SCENARIO_ONBOARDING_STEP,
  SCENARIO_WITH_INVOICES_ONBOARDING_STEP
} from '../../../../predict/src/forecast/onboarding/const';
import { ForecastingNavigationTabs } from '../../../../predict/src/forecast/detail/predictions/navigation/forecasting-navigation-tabs';
import { CreateNewForecastPayload, ForecastType } from '../../../../predict/src/forecast/dashboard/types';
import { createNewForecast } from '../../../../predict/src/core/api/forecast';
import { trackCreateForecastEvent } from '../../../../predict/src/forecast/dashboard/utils';
import { useSelectAndLoadWorkspaces } from '../hooks/use-select-and-load-workspaces';
import { EndOnboardingFlow } from '../../../../predict/src/forecast/onboarding/components/end-onboarding-flow';

export enum ScenarioType {
  WITH_INVOICES = 'with-invoices',
  WITHOUT_INVOICES = 'without-invoices'
}

export const ScenarioSelectType = () => {
  const intl = useIntl();
  const [selectedScenarioType, setSelectedScenarioType] = useState<ScenarioType | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();

  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const workspaceId = searchParams.get(QUERY_PARAMS.WORKSPACE_ID);
  const organisationId = searchParams.get(QUERY_PARAMS.ORG_ID);

  const { isLoading: isLoadingAppData } = useSelectAndLoadWorkspaces();

  const handleNextClick = async () => {
    if (!selectedScenarioType || !workspaceId || !organisationId) {
      return;
    }

    const includeInvoices = selectedScenarioType === ScenarioType.WITH_INVOICES;

    try {
      setIsLoading(true);
      const requestBody: CreateNewForecastPayload = {
        name: intl.formatMessage({ id: 'predict.my_first_forecast', defaultMessage: 'My first forecast' }),
        kind: 'scenario-from-scratch',
        categories_with_ai_enabled: null,
        forecast_from_date: format(new Date(), 'yyyy-MM-dd'),
        include_unpaid_invoices: includeInvoices
      };

      const createdForecast = await createNewForecast(workspaceId, organisationId, requestBody);

      if (selectedScenarioType === ScenarioType.WITH_INVOICES) {
        trackCreateForecastEvent(ForecastType.SCENARIO_WITH_INVOICES);
        localStorage.setItem(LS_FORECASTING_ONBOARDING_TYPE, ONBOARDING_FORECAST_TYPE.SCENARIO_WITH_INVOICES);
      } else {
        trackCreateForecastEvent(ForecastType.SCENARIO);
        localStorage.setItem(LS_FORECASTING_ONBOARDING_TYPE, ONBOARDING_FORECAST_TYPE.SCENARIO);
      }

      if (selectedScenarioType === ScenarioType.WITHOUT_INVOICES) {
        localStorage.setItem(LS_FORECASTING_ONBOARDING_STEP, SCENARIO_ONBOARDING_STEP.SELECT_TYPE);
      } else {
        localStorage.setItem(
          LS_FORECASTING_ONBOARDING_STEP,
          SCENARIO_WITH_INVOICES_ONBOARDING_STEP.INVOICES_BILLS_INCLUDED
        );
      }

      if (includeInvoices) {
        navigate(
          `${getForecastByIdOnboardingUrl(workspaceId, organisationId, createdForecast.id, false, true)}/${
            SCENARIO_WITH_INVOICES_ONBOARDING_STEP.INVOICES_BILLS_INCLUDED
          }?${QUERY_PARAMS.ONBOARDING_FORECAST_ID}=${createdForecast.id}`
        );
      } else {
        navigate(
          `${getForecastByIdOnboardingUrl(workspaceId, organisationId, createdForecast.id, false, includeInvoices)}/${
            SCENARIO_ONBOARDING_STEP.SELECT_TYPE
          }${ROUTES.SALES}?${QUERY_PARAMS.ONBOARDING_FORECAST_ID}=${createdForecast.id}`
        );
      }
    } catch (e) {
      Logger.error(e as string);
      toastError();
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading || isLoadingAppData) {
    return <UILoader isFull />;
  }

  return (
    <>
      <div className={styles.contentContainer}>
        <OnboardingPageTitle
          title={
            <FormattedMessage
              id="onboarding.is_managing_short_term_cash_flow_important"
              defaultMessage="Is managing short term cash flow important?"
            />
          }
          description={
            <FormattedMessage
              id="onboarding.managing_cash_flow_desc"
              defaultMessage="Choose yes, if you want to update future cash flow by being able to manipulate the payment dates of due invoices and bills."
            />
          }
        />
        <div style={{ display: 'flex', marginBottom: 30 }}>
          <div
            className={classNames(styles.onboardingStartButton, styles.onboardingForecastTypeButton, {
              [styles.onboardingStartButtonActive]: selectedScenarioType === ScenarioType.WITH_INVOICES
            })}
            style={{ width: 344 }}
            onClick={() => setSelectedScenarioType(ScenarioType.WITH_INVOICES)}
          >
            {selectedScenarioType === ScenarioType.WITH_INVOICES ? (
              <UISystemIcon iconName="double-check-onboarding" width={28} height={28} />
            ) : (
              <UISystemIcon iconName="e-invoicing" width={28} height={28} />
            )}
            <FormattedMessage
              id="onboarding.include_invoices_and_bills"
              defaultMessage="Yes, include due invoices and bills in my forecast*"
            />
          </div>
          <div
            className={classNames(styles.onboardingStartButton, styles.onboardingForecastTypeButton, {
              [styles.onboardingStartButtonActive]: selectedScenarioType === ScenarioType.WITHOUT_INVOICES
            })}
            style={{ marginLeft: 20, width: 344 }}
            onClick={() => setSelectedScenarioType(ScenarioType.WITHOUT_INVOICES)}
          >
            {selectedScenarioType === ScenarioType.WITHOUT_INVOICES ? (
              <UISystemIcon iconName="double-check-onboarding" width={28} height={28} />
            ) : (
              <UISystemIcon iconName="create-forecast-start" width={28} height={28} />
            )}
            <FormattedMessage
              id="onboarding.without_invoices_and_bills"
              defaultMessage="No, create a traditional 3 year cash flow forecast"
            />
          </div>
          <Button
            disabled={selectedScenarioType === null}
            ml="30px"
            size="large"
            buttonType="primary"
            onClick={handleNextClick}
          >
            <FormattedMessage id="common.next" defaultMessage="Next" />
          </Button>
        </div>
        <div style={{ marginTop: 30 }}>
          <ForecastingNavigationTabs
            disabled
            showInvoices={selectedScenarioType === null || selectedScenarioType === ScenarioType.WITH_INVOICES}
          />
        </div>
      </div>
      <EndOnboardingFlow />
    </>
  );
};
